import { api } from "./api";
import type {
  AdminCompanyBilling,
  AdminOverview,
  AdminSubscriptionRow,
  BillingStatus,
  ChangePreview,
  Grant,
  GrantCreate,
  GrantUpdate,
  Inquiry,
  InquiryCreate,
  Invoice,
  MonthlyRevenuePoint,
  PaymentMethod,
  PlanAdmin,
  PlanPublic,
  PlanUpsert,
  PolicyConfig,
  PortOneConfig,
  PortOneConfigUpsert,
  Subscription,
  EnforcementImpactRow,
} from "@/types/billing";

export const billingService = {
  // ---- 회사관리자 ----
  getPlans: async (): Promise<PlanPublic[]> =>
    (await api.get("/billing/plans")).data,

  getStatus: async (): Promise<BillingStatus> =>
    (await api.get("/billing/status")).data,

  getSubscription: async (): Promise<Subscription | null> =>
    (await api.get("/billing/subscription")).data,

  subscribe: async (plan_id: string, billing_cycle: "monthly" | "annual"): Promise<Subscription> =>
    (await api.post("/billing/subscribe", { plan_id, billing_cycle })).data,

  createInquiry: async (body: InquiryCreate): Promise<{ success: boolean; id: string }> =>
    (await api.post("/billing/inquiries", body)).data,

  getPaymentMethods: async (): Promise<PaymentMethod[]> =>
    (await api.get("/billing/payment-methods")).data,

  registerBillingKey: async (billing_key: string): Promise<PaymentMethod> =>
    (await api.post("/billing/payment-methods", { billing_key })).data,

  deletePaymentMethod: async (id: string): Promise<void> => {
    await api.delete(`/billing/payment-methods/${id}`);
  },

  getInvoices: async (): Promise<Invoice[]> =>
    (await api.get("/billing/invoices")).data,

  previewChange: async (plan_id?: string): Promise<ChangePreview> =>
    (await api.get("/billing/change/preview", { params: plan_id ? { plan_id } : {} })).data,

  applyChange: async (plan_id?: string): Promise<{ ok: boolean; reason?: string; amount?: number }> =>
    (await api.post("/billing/change", { plan_id: plan_id ?? null })).data,

  retryPayment: async (): Promise<{ ok: boolean; reason?: string }> =>
    (await api.post("/billing/retry-payment")).data,

  cancel: async (reason?: string, immediate = false): Promise<Subscription> =>
    (await api.post("/billing/cancel", { reason, immediate })).data,

  reactivate: async (): Promise<Subscription> =>
    (await api.post("/billing/reactivate")).data,

  // ---- 슈퍼관리자 ----
  adminGetPlans: async (): Promise<PlanAdmin[]> =>
    (await api.get("/billing/admin/plans")).data,

  adminCreatePlan: async (body: PlanUpsert): Promise<PlanAdmin> =>
    (await api.post("/billing/admin/plans", body)).data,

  adminUpdatePlan: async (id: string, body: PlanUpsert): Promise<PlanAdmin> =>
    (await api.put(`/billing/admin/plans/${id}`, body)).data,

  adminDeletePlan: async (id: string): Promise<void> => {
    await api.delete(`/billing/admin/plans/${id}`);
  },

  adminPublishPlan: async (id: string, publish: boolean): Promise<PlanAdmin> =>
    (await api.post(`/billing/admin/plans/${id}/publish`, null, { params: { publish } })).data,

  adminGetInquiries: async (): Promise<Inquiry[]> =>
    (await api.get("/billing/admin/inquiries")).data,

  adminUpdateInquiry: async (id: string, status: string): Promise<void> => {
    await api.patch(`/billing/admin/inquiries/${id}`, { status });
  },

  adminGetPortone: async (): Promise<PortOneConfig> =>
    (await api.get("/billing/admin/portone-config")).data,

  adminUpdatePortone: async (body: PortOneConfigUpsert): Promise<PortOneConfig> =>
    (await api.put("/billing/admin/portone-config", body)).data,

  adminGetPolicy: async (): Promise<PolicyConfig> =>
    (await api.get("/billing/admin/policy")).data,

  adminUpdatePolicy: async (body: Partial<PolicyConfig>): Promise<PolicyConfig> =>
    (await api.put("/billing/admin/policy", body)).data,

  adminGetEnforcementImpact: async (): Promise<EnforcementImpactRow[]> =>
    (await api.get("/billing/admin/enforcement-impact")).data,

  adminGetDashboard: async (): Promise<AdminOverview> =>
    (await api.get("/billing/admin/dashboard")).data,

  adminGetSubscriptions: async (): Promise<AdminSubscriptionRow[]> =>
    (await api.get("/billing/admin/subscriptions")).data,

  adminGetCompany: async (companyId: string): Promise<AdminCompanyBilling> =>
    (await api.get(`/billing/admin/subscriptions/${companyId}`)).data,

  adminCharge: async (subId: string): Promise<{ ok: boolean; reason?: string }> =>
    (await api.post(`/billing/admin/subscriptions/${subId}/charge`)).data,

  adminExtend: async (subId: string, days: number): Promise<{ ok: boolean }> =>
    (await api.post(`/billing/admin/subscriptions/${subId}/extend`, { days })).data,

  adminGrant: async (
    companyId: string,
    body: { plan_id: string; billing_cycle: "monthly" | "annual"; status: "active" | "trialing"; period_days?: number | null },
  ): Promise<Subscription> =>
    (await api.post(`/billing/admin/subscriptions/${companyId}/grant`, body)).data,

  adminSetStatus: async (subId: string, status: string): Promise<{ ok: boolean }> =>
    (await api.post(`/billing/admin/subscriptions/${subId}/status`, { status })).data,

  // ---- 구독 기간 부여/결제 원장 ----
  adminGetRevenueMonthly: async (months = 12): Promise<MonthlyRevenuePoint[]> =>
    (await api.get("/billing/admin/revenue/monthly", { params: { months } })).data,

  adminListGrants: async (companyId: string): Promise<Grant[]> =>
    (await api.get(`/billing/admin/subscriptions/${companyId}/grants`)).data,

  adminCreateGrant: async (companyId: string, body: GrantCreate): Promise<Grant> =>
    (await api.post(`/billing/admin/subscriptions/${companyId}/grants`, body)).data,

  adminCancelGrant: async (grantId: string, reason?: string): Promise<Grant> =>
    (await api.post(`/billing/admin/grants/${grantId}/cancel`, { reason })).data,

  adminUpdateGrant: async (grantId: string, body: GrantUpdate): Promise<Grant> =>
    (await api.patch(`/billing/admin/grants/${grantId}`, body)).data,
};

export default billingService;
